import { useState } from "react";
import {
  useHelpSettings,
  useUpdateHelpSettings,
  useHelpCategories,
  useAdminHelpArticles,
  useCreateHelpArticle,
  useUpdateHelpArticle,
  useDeleteHelpArticle,
  useDuplicateHelpArticle,
  useArchiveHelpArticle,
  useCreateHelpCategory,
  useUpdateHelpCategory,
  useDeleteHelpCategory,
  HelpCategory,
  HelpArticle,
} from "@/hooks/useHelpCenter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  FolderOpen,
  Settings,
  GraduationCap,
  Users,
  User,
  Copy,
  Archive,
  ArchiveRestore,
  FileCode,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { HELP_ARTICLE_TEMPLATE, TEMPLATE_GUIDANCE } from "@/lib/helpArticleTemplate";

function GlobalSettings() {
  const { data: settings, isLoading } = useHelpSettings();
  const updateSettings = useUpdateHelpSettings();

  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Global Help Center Settings
        </CardTitle>
        <CardDescription>
          Control the visibility and availability of the Help Center across the platform.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-base">Help Center Enabled</Label>
            <p className="text-sm text-muted-foreground">
              When disabled, users will see a friendly "unavailable" message.
            </p>
          </div>
          <Switch
            checked={settings?.help_enabled}
            onCheckedChange={(checked) => {
              updateSettings.mutate({ help_enabled: checked }, {
                onSuccess: () => toast.success(checked ? "Help Center enabled" : "Help Center disabled"),
              });
            }}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-base">Show in Navigation</Label>
            <p className="text-sm text-muted-foreground">
              Display Help link in the main navigation menu.
            </p>
          </div>
          <Switch
            checked={settings?.show_in_navigation}
            onCheckedChange={(checked) => {
              updateSettings.mutate({ show_in_navigation: checked }, {
                onSuccess: () => toast.success("Navigation setting updated"),
              });
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryManager() {
  const { data: categories, isLoading } = useHelpCategories();
  const createCategory = useCreateHelpCategory();
  const updateCategory = useUpdateHelpCategory();
  const deleteCategory = useDeleteHelpCategory();
  const [editingCategory, setEditingCategory] = useState<HelpCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "HelpCircle",
    sort_order: 0,
    is_active: true,
  });

  const handleCreate = () => {
    createCategory.mutate(formData, {
      onSuccess: () => {
        toast.success("Category created");
        setIsCreating(false);
        setFormData({ name: "", description: "", icon: "HelpCircle", sort_order: 0, is_active: true });
      },
      onError: (error) => toast.error("Failed to create category"),
    });
  };

  const handleUpdate = () => {
    if (!editingCategory) return;
    updateCategory.mutate({ id: editingCategory.id, ...formData }, {
      onSuccess: () => {
        toast.success("Category updated");
        setEditingCategory(null);
      },
      onError: () => toast.error("Failed to update category"),
    });
  };

  const handleDelete = (id: string) => {
    deleteCategory.mutate(id, {
      onSuccess: () => toast.success("Category deleted"),
      onError: () => toast.error("Failed to delete category. Make sure no articles are in this category."),
    });
  };

  const openEditDialog = (category: HelpCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      icon: category.icon || "HelpCircle",
      sort_order: category.sort_order,
      is_active: category.is_active,
    });
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Categories ({categories?.length || 0})</h3>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>

      <div className="grid gap-3">
        {categories?.map((category) => (
          <Card key={category.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge variant={category.is_active ? "default" : "secondary"}>
                    {category.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <div>
                    <h4 className="font-medium">{category.name}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {category.description || "No description"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(category)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Category</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{category.name}"? All articles in this category will also be deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(category.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreating || !!editingCategory} onOpenChange={(open) => {
        if (!open) {
          setIsCreating(false);
          setEditingCategory(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "Create Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Category name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
              />
            </div>
            <div>
              <Label>Icon</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Rocket", "User", "LayoutDashboard", "Users", "GraduationCap", "BookOpen", "Calendar", "MessageSquare", "Shield", "HelpCircle"].map(icon => (
                    <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsCreating(false); setEditingCategory(null); }}>
              Cancel
            </Button>
            <Button onClick={editingCategory ? handleUpdate : handleCreate}>
              {editingCategory ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ArticleManager() {
  const { data: articles, isLoading } = useAdminHelpArticles();
  const { data: categories } = useHelpCategories();
  const createArticle = useCreateHelpArticle();
  const updateArticle = useUpdateHelpArticle();
  const deleteArticle = useDeleteHelpArticle();
  const duplicateArticle = useDuplicateHelpArticle();
  const archiveArticle = useArchiveHelpArticle();
  
  const [filter, setFilter] = useState<{ category: string; visibility: string; status: string; archived: string }>({
    category: "all",
    visibility: "all",
    status: "all",
    archived: "active", // Default to showing only active (non-archived) articles
  });
  const [editingArticle, setEditingArticle] = useState<(HelpArticle & { category: HelpCategory }) | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [previewRole, setPreviewRole] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    category_id: "",
    title: "",
    summary: "",
    body: "",
    visibility: "all" as 'all' | 'mentor' | 'entrepreneur' | 'hidden',
    is_published: false,
    sort_order: 0,
  });

  const filteredArticles = articles?.filter(article => {
    if (filter.category !== "all" && article.category_id !== filter.category) return false;
    if (filter.visibility !== "all" && article.visibility !== filter.visibility) return false;
    if (filter.status === "published" && !article.is_published) return false;
    if (filter.status === "draft" && article.is_published) return false;
    if (filter.archived === "active" && article.is_archived) return false;
    if (filter.archived === "archived" && !article.is_archived) return false;
    return true;
  });

  const handleDuplicate = (article: HelpArticle & { category: HelpCategory }) => {
    duplicateArticle.mutate(article, {
      onSuccess: () => toast.success("Article duplicated as draft"),
      onError: () => toast.error("Failed to duplicate article"),
    });
  };

  const handleArchive = (id: string, archive: boolean) => {
    archiveArticle.mutate({ id, archive }, {
      onSuccess: () => toast.success(archive ? "Article archived" : "Article restored"),
      onError: () => toast.error(archive ? "Failed to archive article" : "Failed to restore article"),
    });
  };

  const handleCreate = () => {
    if (!formData.category_id) {
      toast.error("Please select a category");
      return;
    }
    createArticle.mutate(formData, {
      onSuccess: () => {
        toast.success("Article created");
        setIsCreating(false);
        resetForm();
      },
      onError: () => toast.error("Failed to create article"),
    });
  };

  const handleUpdate = () => {
    if (!editingArticle) return;
    updateArticle.mutate({ id: editingArticle.id, ...formData }, {
      onSuccess: () => {
        toast.success("Article updated");
        setEditingArticle(null);
      },
      onError: () => toast.error("Failed to update article"),
    });
  };

  const handleDelete = (id: string) => {
    deleteArticle.mutate(id, {
      onSuccess: () => toast.success("Article deleted"),
      onError: () => toast.error("Failed to delete article"),
    });
  };

  const resetForm = () => {
    setFormData({
      category_id: "",
      title: "",
      summary: "",
      body: "",
      visibility: "all",
      is_published: false,
      sort_order: 0,
    });
  };

  const initializeWithTemplate = () => {
    setFormData({
      category_id: "",
      title: "",
      summary: "",
      body: HELP_ARTICLE_TEMPLATE,
      visibility: "all",
      is_published: false,
      sort_order: 0,
    });
  };

  const openEditDialog = (article: HelpArticle & { category: HelpCategory }) => {
    setEditingArticle(article);
    setFormData({
      category_id: article.category_id,
      title: article.title,
      summary: article.summary || "",
      body: article.body,
      visibility: article.visibility,
      is_published: article.is_published,
      sort_order: article.sort_order,
    });
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'mentor': return <GraduationCap className="h-3 w-3" />;
      case 'entrepreneur': return <User className="h-3 w-3" />;
      case 'hidden': return <EyeOff className="h-3 w-3" />;
      default: return <Users className="h-3 w-3" />;
    }
  };

  const getVisibilityColor = (visibility: string) => {
    switch (visibility) {
      case 'mentor': return 'bg-blue-500/10 text-blue-600 border-blue-200';
      case 'entrepreneur': return 'bg-green-500/10 text-green-600 border-green-200';
      case 'hidden': return 'bg-gray-500/10 text-gray-600 border-gray-200';
      default: return 'bg-purple-500/10 text-purple-600 border-purple-200';
    }
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.visibility} onValueChange={(v) => setFilter({ ...filter, visibility: v })}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Visibility</SelectItem>
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="mentor">Mentors Only</SelectItem>
              <SelectItem value="entrepreneur">Entrepreneurs Only</SelectItem>
              <SelectItem value="hidden">Hidden</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.archived} onValueChange={(v) => setFilter({ ...filter, archived: v })}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Archive" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Articles</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="archived">Archived Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setIsCreating(true); resetForm(); }}>
            <Plus className="h-4 w-4 mr-2" />
            Blank Article
          </Button>
          <Button onClick={() => { setIsCreating(true); initializeWithTemplate(); }}>
            <FileCode className="h-4 w-4 mr-2" />
            New from Template
          </Button>
        </div>
      </div>

      <div className="grid gap-3">
        {filteredArticles?.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No articles found matching your filters.
            </CardContent>
          </Card>
        ) : (
          filteredArticles?.map((article) => (
            <Card key={article.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant={article.is_published ? "default" : "secondary"}>
                        {article.is_published ? "Published" : "Draft"}
                      </Badge>
                      {article.is_archived && (
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
                          <Archive className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                      <Badge variant="outline" className={getVisibilityColor(article.visibility)}>
                        {getVisibilityIcon(article.visibility)}
                        <span className="ml-1 capitalize">{article.visibility}</span>
                      </Badge>
                      <Badge variant="outline">
                        <FolderOpen className="h-3 w-3 mr-1" />
                        {article.category?.name}
                      </Badge>
                    </div>
                    <h4 className="font-medium">{article.title}</h4>
                    {article.summary && (
                      <p className="text-sm text-muted-foreground line-clamp-1">{article.summary}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Updated {format(new Date(article.updated_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDuplicate(article)}
                      title="Duplicate article"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(article)} title="Edit article">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {article.is_archived ? (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleArchive(article.id, false)}
                        title="Restore article"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleArchive(article.id, true)}
                        title="Archive article"
                        className="text-amber-600"
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-destructive" title="Delete article">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Article</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{article.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(article.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Article Dialog */}
      <Dialog open={isCreating || !!editingArticle} onOpenChange={(open) => {
        if (!open) {
          setIsCreating(false);
          setEditingArticle(null);
          setPreviewRole(null);
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? "Edit Article" : "Create Article"}</DialogTitle>
            <DialogDescription>
              {editingArticle 
                ? "Update the article details below." 
                : "Use the template structure for consistent formatting across all Help Center articles."}
            </DialogDescription>
          </DialogHeader>

          {previewRole ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="outline">
                  Previewing as: {previewRole === 'entrepreneur' ? 'Entrepreneur' : 'Mentor'}
                </Badge>
                <Button variant="outline" onClick={() => setPreviewRole(null)}>
                  Back to Edit
                </Button>
              </div>
              {/* Preview with production styling */}
              <div className="bg-background rounded-lg border">
                <div className="px-6 py-6 border-b bg-muted/30">
                  <p className="text-sm text-muted-foreground mb-2">Help Center • Preview</p>
                  <h2 className="text-xl font-bold text-foreground mb-2">{formData.title || "Untitled Article"}</h2>
                  {formData.summary && (
                    <p className="text-muted-foreground">{formData.summary}</p>
                  )}
                </div>
                <div className="px-6 py-6">
                  <div 
                    className="help-article-content prose prose-slate max-w-none
                      prose-headings:text-foreground prose-headings:font-semibold
                      prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-border
                      prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                      prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
                      prose-ul:my-4 prose-ul:space-y-2
                      prose-ol:my-4 prose-ol:space-y-2
                      prose-li:text-muted-foreground prose-li:leading-relaxed
                      prose-strong:text-foreground prose-strong:font-semibold
                      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                      [&_.help-article-intro]:text-base [&_.help-article-intro]:text-muted-foreground [&_.help-article-intro]:leading-relaxed [&_.help-article-intro]:mb-6
                      [&_.help-callout]:my-6 [&_.help-callout]:p-4 [&_.help-callout]:bg-info/5 [&_.help-callout]:border-l-4 [&_.help-callout]:border-l-info [&_.help-callout]:rounded-r
                      [&_.help-callout_p]:text-foreground [&_.help-callout_p]:mb-0
                      [&_.help-footer-note]:mt-8 [&_.help-footer-note]:pt-6 [&_.help-footer-note]:border-t [&_.help-footer-note]:border-border
                      [&_.help-footer-note_p]:text-sm [&_.help-footer-note_p]:text-muted-foreground"
                    dangerouslySetInnerHTML={{ __html: formData.body }} 
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Admin guidance note for new articles */}
              {isCreating && formData.body.includes('help-article') && (
                <div className="p-3 bg-info/10 border border-info/20 rounded-lg text-sm">
                  <p className="font-medium text-foreground mb-1">📝 Template Loaded</p>
                  <p className="text-muted-foreground">
                    Replace the placeholder text in the body with your content. Use H2 headings for sections, 
                    keep paragraphs short, and avoid marketing language.
                  </p>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select
                    value={formData.visibility}
                    onValueChange={(v) => setFormData({ ...formData, visibility: v as any })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      <SelectItem value="mentor">Mentors Only</SelectItem>
                      <SelectItem value="entrepreneur">Entrepreneurs Only</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={TEMPLATE_GUIDANCE.title}
                />
              </div>

              <div>
                <Label>Summary</Label>
                <Textarea
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder={TEMPLATE_GUIDANCE.summary}
                  rows={2}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Body Content (HTML) *</Label>
                  {!formData.body && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => setFormData({ ...formData, body: HELP_ARTICLE_TEMPLATE })}
                    >
                      <FileCode className="h-3 w-3 mr-1" />
                      Insert Template
                    </Button>
                  )}
                </div>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="<article class='help-article'>&#10;  <div class='help-article-intro'>&#10;    <p>Introduction...</p>&#10;  </div>&#10;  <h2>Section Title</h2>&#10;  <p>Content...</p>&#10;</article>"
                  rows={16}
                  className="font-mono text-sm"
                />
                <div className="mt-2 p-3 bg-muted/50 rounded text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">Formatting Guide:</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;h2&gt;</code> for section headings</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;p&gt;</code> for paragraphs</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;ul&gt;&lt;li&gt;</code> for bullet lists, <code className="bg-muted px-1 rounded">&lt;ol&gt;&lt;li&gt;</code> for numbered lists</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;strong&gt;</code> for bold, <code className="bg-muted px-1 rounded">&lt;em&gt;</code> for italic</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;div class="help-callout"&gt;</code> for important notes</p>
                  <p>• <code className="bg-muted px-1 rounded">&lt;a href="..."&gt;</code> for links</p>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Sort Order</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_published}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                  />
                  <Label>Published</Label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!previewRole && (
              <div className="flex gap-2 mr-auto">
                <Button variant="outline" size="sm" onClick={() => setPreviewRole('entrepreneur')}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview as Entrepreneur
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPreviewRole('mentor')}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview as Mentor
                </Button>
              </div>
            )}
            <Button variant="outline" onClick={() => { setIsCreating(false); setEditingArticle(null); setPreviewRole(null); }}>
              Cancel
            </Button>
            {!previewRole && (
              <Button onClick={editingArticle ? handleUpdate : handleCreate}>
                {editingArticle ? "Save Changes" : "Create Article"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function HelpCenterManager() {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold">Help Center Management</h2>
          <p className="text-muted-foreground">
            Manage help articles, categories, and global settings for the Help Center.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/help/categories")}
            className="gap-2"
          >
            <FolderOpen className="h-4 w-4" />
            Manage Categories
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/help/articles")}
            className="gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            Full Article Manager
          </Button>
        </div>
      </div>

      <Tabs defaultValue="articles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="articles" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Articles
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="articles">
          <ArticleManager />
        </TabsContent>

        <TabsContent value="categories">
          <CategoryManager />
        </TabsContent>

        <TabsContent value="settings">
          <GlobalSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
